import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useFormat } from '../../context/FormatContext';

const { FiInfo, FiToggleLeft, FiToggleRight, FiAlertCircle } = FiIcons;

const frontMatterDefs = [
  {
    id: 'title_page',
    label: 'Title Page',
    description: 'Automatically generated from your book metadata. No input needed.',
    auto: true,
    fields: [],
  },
  {
    id: 'copyright',
    label: 'Copyright Page',
    description: 'Auto-generated with your name and year. Add custom copyright text below.',
    auto: false,
    fields: [
      { key: 'custom_copyright', label: 'Custom Copyright Text (optional)', type: 'textarea',
        placeholder: 'e.g. All rights reserved. No part of this book may be reproduced...' },
      { key: 'publisher_note', label: 'Publisher / Imprint Name (optional)', type: 'text',
        placeholder: 'e.g. Graceway Publishing' },
      { key: 'isbn', label: 'ISBN (optional)', type: 'text', placeholder: 'e.g. 978-0-000000-00-0' },
      { key: 'edition', label: 'Edition Note (optional)', type: 'text', placeholder: 'e.g. First Edition, 2025' },
    ],
  },
  {
    id: 'dedication',
    label: 'Dedication',
    description: 'A personal dedication to someone meaningful to you.',
    auto: false,
    fields: [
      { key: 'dedication_text', label: 'Dedication Text', type: 'textarea',
        placeholder: 'e.g. To my mother, who taught me that words have power...' },
    ],
  },
  {
    id: 'foreword',
    label: 'Foreword',
    description: 'Written by someone other than the author — a colleague, mentor, or endorser.',
    auto: false,
    fields: [
      { key: 'foreword_author', label: 'Foreword Written By', type: 'text',
        placeholder: 'e.g. Dr. Jane Smith' },
      { key: 'foreword_author_title', label: 'Their Title / Credentials', type: 'text',
        placeholder: 'e.g. Pastor, Grace Community Church' },
      { key: 'foreword_body', label: 'Foreword Body Text', type: 'textarea',
        placeholder: 'Paste or type the foreword content here...' },
    ],
  },
  {
    id: 'toc',
    label: 'Table of Contents',
    description: "Auto-generated from your chapter structure. E-reader navigation is also built in.",
    auto: true,
    fields: [],
  },
];

const FrontMatterEditor = () => {
  const { pipelineState, updateFrontMatterContent, updateStructure } = useFormat();
  const { structure, frontMatterContent = {} } = pipelineState;

  if (!structure) return null;

  const includedIds = new Set(
    structure.front_matter.filter(i => i.included !== false).map(i => i.id)
  );

  const handleToggle = (id, checked) => {
    updateStructure('front_matter', id, checked);
  };

  const handleField = (sectionId, fieldKey, value) => {
    updateFrontMatterContent(sectionId, fieldKey, value);
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-start space-x-3">
        <SafeIcon icon={FiInfo} className="text-blue-500 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-blue-800 font-medium">
          Toggle each section on to include it in your EPUB. Fill in the fields below to customize the content that appears in your exported file.
        </p>
      </div>

      {frontMatterDefs.map((section, idx) => {
        const isIncluded = includedIds.has(section.id);
        const sectionData = frontMatterContent[section.id] || {};

        return (
          <motion.div
            key={section.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.05 }}
            className={`bg-white rounded-2xl border-2 overflow-hidden transition-all ${
              isIncluded ? 'border-primary/30 shadow-md' : 'border-slate-100 opacity-60'
            }`}
          >
            {/* Header Row */}
            <div className="flex items-center justify-between px-6 py-4 bg-slate-50 border-b border-slate-100">
              <div>
                <h3 className="font-bold text-slate-800">{section.label}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{section.description}</p>
              </div>
              <label className="flex items-center cursor-pointer ml-4">
                <div className="relative">
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={isIncluded}
                    onChange={e => handleToggle(section.id, e.target.checked)}
                  />
                  <div className={`block w-12 h-7 rounded-full transition-colors ${isIncluded ? 'bg-primary' : 'bg-slate-300'}`}></div>
                  <div className={`dot absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition-transform shadow ${isIncluded ? 'transform translate-x-5' : ''}`}></div>
                </div>
              </label>
            </div>

            {/* Fields */}
            <AnimatePresence>
              {isIncluded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="p-6">
                    {section.auto ? (
                      <div className="flex items-center space-x-2 text-sm text-slate-500 bg-slate-50 rounded-lg p-3">
                        <SafeIcon icon={FiInfo} className="text-slate-400" />
                        <span>This section is automatically generated from your book metadata.</span>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {section.fields.map(field => (
                          <div key={field.key}>
                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">
                              {field.label}
                            </label>
                            {field.type === 'textarea' ? (
                              <textarea
                                value={sectionData[field.key] || ''}
                                onChange={e => handleField(section.id, field.key, e.target.value)}
                                placeholder={field.placeholder}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-y min-h-[120px]"
                              />
                            ) : (
                              <input
                                type="text"
                                value={sectionData[field.key] || ''}
                                onChange={e => handleField(section.id, field.key, e.target.value)}
                                placeholder={field.placeholder}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        );
      })}
    </div>
  );
};

export default FrontMatterEditor;